// 사용량 세는 규칙(순수) — 브라우저(설정 화면)도 읽으므로 요청 런타임(redirect-error → async_hooks)에 닿지 않는다(node 가 타입만 지워 읽으니 enum·namespace 금지).

import { kstMonthRange } from "../customer-notify/month";
import type { CustomerNotifyMonthCount } from "../customer-notify/types";
import type { ProjectStatus } from "../project/types";

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 설정 화면·스크립트 기본 개월 수 · 스크립트 상한.
export const USAGE_MONTHS = 3;
export const USAGE_MONTHS_MAX = 24;

// 베타 성공 기준 — 한 달 견적 발송 5건.
export const BETA_MONTHLY_SENDS = 5;

// 사용량의 「진행 현장」 — 시공대기+시공중(과금 축 ACTIVE_SITE_STATUSES 는 시공중만이라 다르다).
export const USAGE_SITE_STATUSES = ["SCHEDULED", "IN_PROGRESS"] as const satisfies readonly ProjectStatus[];

export interface UsageRange {
  from: string;
  to: string;
}

export interface UsageMonthRange extends UsageRange {
  // KST 기준 `YYYY-MM`.
  month: string;
}

export interface UsageSendFact {
  id: string;
  sent_at: string;
}

export interface UsageProjectFact {
  id: string;
  status: ProjectStatus;
  created_at: string;
  completion_date: string | null;
}

export interface UsagePhotoFact {
  id: string;
  created_at: string;
  size_bytes: number;
  uploaded_by: string | null;
}

export interface UsageActorFact {
  id: string;
  actor_id: string | null;
  created_at: string;
}

// 저장소가 넘기는 날것 — 범위는 좁히기용일 뿐이고 달·상태·null 거르기는 orgUsageOf 가 다시 한다.
export interface OrgUsageFacts {
  sends: UsageSendFact[];
  projects: UsageProjectFact[];
  photos: UsagePhotoFact[];
  actors: UsageActorFact[];
}

export interface OrgUsageMonth extends UsageMonthRange {
  quoteSends: number;
  siteMax: number;
  notify: CustomerNotifyMonthCount;
  photoCount: number;
  photoBytes: number;
  activeMembers: number;
}

export interface OrgUsage {
  // 최근 달이 앞.
  months: OrgUsageMonth[];
  sitesNow: number;
  // 지금 시공중(IN_PROGRESS)인 현장 수 — 0 이면 무입력 감지에서 「쉬는 중」.
  inProgressNow: number;
}

const monthKeyOf = (from: string): string => {
  const kst = new Date(Date.parse(from) + KST_OFFSET_MS);
  return `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, "0")}`;
};

// now 가 든 달부터 거슬러 count 개(최근 달이 앞) — 1 미만·상한 초과는 잘라 낸다.
export const usageMonths = (now: Date, count: number = USAGE_MONTHS): UsageMonthRange[] => {
  const n = Math.min(USAGE_MONTHS_MAX, Math.max(1, Math.floor(count)));
  const out: UsageMonthRange[] = [];
  let at = now.toISOString();
  for (let i = 0; i < n; i += 1) {
    const range = kstMonthRange(at);
    out.push({ month: monthKeyOf(range.from), ...range });
    at = new Date(Date.parse(range.from) - 1).toISOString();
  }
  return out;
};

// 여러 달을 한 번에 읽을 창 — 가장 오래된 달 시작부터 가장 최근 달 끝까지.
export const usageWindow = (months: readonly UsageMonthRange[]): UsageRange => ({
  from: months[months.length - 1].from,
  to: months[0].to,
});

// 창 시작의 KST 날짜 — 준공일(date 열)로 현장을 좁힐 때 쓴다.
export const kstDateOf = (iso: string): string => new Date(Date.parse(iso) + KST_OFFSET_MS).toISOString().slice(0, 10);

const inRange = (iso: string, from: number, to: number): boolean => {
  const t = Date.parse(iso);
  return Number.isFinite(t) && t >= from && t < to;
};

// 현장이 「진행」이던 구간 [생성, 준공일 다음날 00:00 KST) — 아직 진행이면 끝이 없다.
const siteSpan = (p: UsageProjectFact): [number, number] => {
  const start = Date.parse(p.created_at);
  if ((USAGE_SITE_STATUSES as readonly ProjectStatus[]).includes(p.status)) return [start, Number.POSITIVE_INFINITY];
  if (!p.completion_date) return [start, start];
  const [y, m, d] = p.completion_date.slice(0, 10).split("-").map(Number);
  return [start, Date.UTC(y, m - 1, d + 1) - KST_OFFSET_MS];
};

// 달 안에서 동시에 진행이던 현장 수의 최대 — 이번 달은 지금까지만 본다.
const siteMaxIn = (projects: readonly UsageProjectFact[], from: number, to: number): number => {
  if (to <= from) return 0;
  let open = 0;
  const events: Array<[number, number]> = [];
  for (const p of projects) {
    const [start, end] = siteSpan(p);
    if (!Number.isFinite(start) || end <= start || start >= to || end <= from) continue;
    if (start <= from) open += 1;
    else events.push([start, 1]);
    if (end < to) events.push([end, -1]);
  }
  // 같은 순간이면 끝난 현장을 먼저 뺀다(구간은 끝을 포함하지 않는다).
  events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let max = open;
  for (const [, delta] of events) {
    open += delta;
    if (open > max) max = open;
  }
  return max;
};

const ZERO_NOTIFY: CustomerNotifyMonthCount = { sent: 0, logOnly: 0 };

// 달마다 한 줄 — 알림 건수는 기존 월 집계(countThisMonth)를 달 키로 받아 그대로 싣는다.
export const orgUsageOf = (input: {
  facts: OrgUsageFacts;
  notify: Readonly<Record<string, CustomerNotifyMonthCount>>;
  months: readonly UsageMonthRange[];
  now: Date;
}): OrgUsage => {
  const { facts } = input;
  const nowMs = input.now.getTime();
  const months = input.months.map((range): OrgUsageMonth => {
    const from = Date.parse(range.from);
    const to = Date.parse(range.to);
    const photos = facts.photos.filter((p) => inRange(p.created_at, from, to));
    const members = new Set<string>();
    for (const a of facts.actors) if (a.actor_id && inRange(a.created_at, from, to)) members.add(a.actor_id);
    for (const p of photos) if (p.uploaded_by) members.add(p.uploaded_by);
    return {
      ...range,
      quoteSends: new Set(facts.sends.filter((s) => inRange(s.sent_at, from, to)).map((s) => s.id)).size,
      siteMax: siteMaxIn(facts.projects, from, Math.min(to, nowMs)),
      notify: input.notify[range.month] ?? ZERO_NOTIFY,
      photoCount: photos.length,
      photoBytes: photos.reduce((sum, p) => sum + (Number(p.size_bytes) || 0), 0),
      activeMembers: members.size,
    };
  });
  const active: readonly ProjectStatus[] = USAGE_SITE_STATUSES;
  return {
    months,
    sitesNow: facts.projects.filter((p) => active.includes(p.status)).length,
    inProgressNow: facts.projects.filter((p) => p.status === "IN_PROGRESS").length,
  };
};

// 무입력 경고 기준 — 직원 활동·현장일지·사진이 이만큼(일) 없으면 운영자가 연락한다.
export const IDLE_DAYS = 7;

export type InputIdleState = "active" | "idle" | "resting";

export interface InputIdle {
  lastInputAt: string | null;
  // 마지막 입력부터 지금까지 꽉 찬 날 수 — 입력이 없으면 null.
  idleDays: number | null;
  state: InputIdleState;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// 마지막 입력 출처 한 벌 — 0083 rpc_admin_org_facts(SQL · migrations.test 가 맞춰 본다)·usage-query fetchLastInputAt·admin/rules lastInputFromRows 가 이 목록을 따른다.
export const LAST_INPUT_SOURCES = [
  { table: "activity", factsKey: "activity_at", actorRequired: true, label: "활동" },
  { table: "project_log", factsKey: "log_at", actorRequired: false, label: "현장일지" },
  { table: "project_photo", factsKey: "photo_at", actorRequired: false, label: "사진" },
] as const;

// 여러 표의 최근 시각 중 가장 늦은 것 — 못 읽는 값은 건너뛴다.
export const lastInputOf = (times: readonly (string | null | undefined)[]): string | null => {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const at of times) {
    const t = at ? Date.parse(at) : Number.NaN;
    if (Number.isFinite(t) && t > bestMs) {
      best = at as string;
      bestMs = t;
    }
  }
  return best;
};

// 시공중 현장이 없으면 「쉬는 중」 · 있는데 IDLE_DAYS 이상(또는 한 번도) 입력이 없으면 「무입력」.
export const inputIdleOf = (input: { lastInputAt: string | null; inProgress: number; now: Date }): InputIdle => {
  const t = input.lastInputAt ? Date.parse(input.lastInputAt) : Number.NaN;
  const idleDays = Number.isFinite(t) ? Math.max(0, Math.floor((input.now.getTime() - t) / DAY_MS)) : null;
  const lastInputAt = Number.isFinite(t) ? input.lastInputAt : null;
  if (input.inProgress <= 0) return { lastInputAt, idleDays, state: "resting" };
  return { lastInputAt, idleDays, state: idleDays === null || idleDays >= IDLE_DAYS ? "idle" : "active" };
};

// `12.3 MB` — 1024 단위, 소수 한 자리(바이트는 정수).
export const formatBytes = (bytes: number): string => {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = Math.max(0, bytes);
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return i === 0 ? `${Math.round(value)} B` : `${value.toFixed(1)} ${units[i]}`;
};
